/* ============================================================
   FGO HEADER — global behaviour
   ------------------------------------------------------------
   Split out of pdp-v2.js so the docking header works on every
   template. It used to bail immediately unless <body> carried
   the `pdp-v2` class, which only the product template sets.

   Owns three things: the measured promo-bar offset, the dock
   hysteresis, and the logo colour swap. Paired with
   fgo-header.css.
   ============================================================ */
/* ============================================================
   HEADER dock — hysteresis + measured promo height
   ------------------------------------------------------------
   The theme toggles .bg at a single threshold, so the header
   flickers when a scroll settles exactly on the boundary. The
   demo docks at one value and releases at a lower one, which
   cannot oscillate.

   Also measures the promo bar so the docked header sits under it
   rather than at a hardcoded offset.
   ============================================================ */
(function () {
  'use strict';

  var head = document.querySelector('#header');
  var promo = document.querySelector('[id*="theme-announcement"]');
  if (!head) return;

  /* --promo-h drives where the header sticks: top = --promo-h + 12 at rest,
     top = --promo-h when docked. Getting it wrong by writing 0 parks the
     header at the very top of the viewport, UNDER a promo bar that is
     z-index 101 — which is the bar sitting on top of the mark.

     This used to measure once, synchronously, at script execution, and then
     only again on resize. Run before the announcement bar has laid out,
     getBoundingClientRect().height returns 0, and 0 is what got written. The
     `var(--promo-h, 40px)` fallback in the CSS cannot save that: the property
     IS set, just set to zero, so the fallback never applies. The header then
     stayed under the bar for the life of the page unless you happened to
     resize the window, which is why it looked fine sometimes.

     Two guards. Never write a 0 while the element exists — remove the
     property instead, so the CSS fallback takes over. And keep measuring:
     on load, on font swap, and via ResizeObserver, since the bar is a
     rotating-message widget whose height can change after first paint. */
  /* 27 Sep 2026 (perf audit, fgo-header.js forced layout, P1). READ, THEN WRITE, in every block of
     this file. On the phone lab (4x CPU, sandbox home) this script forced two whole-page style
     recalculations while it ran, 2,560 elements each, 169 + 171 ms (trace stacks): setPromoH wrote
     --promo-h on <html> and the next line read window.scrollY, which makes Chrome restyle and lay out
     the page on the spot; then the offset block below wrote --fgo-promo-h and measurePull read
     scrollY again. Now:
       - the first run of each block only READS while the script runs; what it measured is written
         in a microtask queued behind it (afterReads), so every read in the file comes before any
         write and the page's next frame styles the writes once;
       - a variable is not rewritten when <html> already carries that value (an inline-style read,
         which costs no layout);
       - later re-measures (load, font swap, resize) read just AFTER a painted frame, when layout is
         already clean, and the ResizeObservers take the size from their entry instead of asking for
         a rect. (Read at the start of a frame instead, the first reader paid the whole pending
         restyle: 2,619 elements, 194 ms at 4x, whichever script it was.) A size change of the promo
         bar itself still lands in its own frame, through the ResizeObserver.
     Measured (interleaved A/B, sandbox home, 4x CPU, LoAF forced style/layout attributed to this
     file over load + scroll + drawer or Shop menu, per session): final code, n=3 per arm, phone
     942 -> 25 ms, desktop 977 -> 19 ms; earlier builds of the same fixes on the phone, n=5,
     650 -> 21 and 864 -> 46 ms. */
  var root = document.documentElement;
  var afterReads = window.queueMicrotask ? function (fn) { window.queueMicrotask(fn); } : function (fn) { Promise.resolve().then(fn); };
  var setVar = function (name, v) {   /* v '' = remove the property */
    if (root.style.getPropertyValue(name) === v) return;
    if (v) root.style.setProperty(name, v); else root.style.removeProperty(name);
  };
  var readPromoH = function () { return promo ? Math.round(promo.getBoundingClientRect().height) : 0; };
  var setPromoH = function (h) { setVar('--promo-h', !promo ? '0px' : (h > 0 ? h + 'px' : '')); };
  var afterFrame = function (fn) { var done = false; var run = function () { if (!done) { done = true; fn(); } }; requestAnimationFrame(function () { setTimeout(run, 0); }); setTimeout(run, 120); };
  var promoT = false;
  var remeasure = function () {
    if (promoT) return;
    promoT = true;
    afterFrame(function () { promoT = false; setPromoH(readPromoH()); });
  };
  var promoH0 = readPromoH();
  addEventListener('resize', remeasure, { passive: true });
  addEventListener('load', remeasure);
  if (document.readyState === 'loading') {
    addEventListener('DOMContentLoaded', remeasure);
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(remeasure).catch(function () {});
  }
  if (promo && window.ResizeObserver) {
    new ResizeObserver(function (entries) {
      var e = entries[entries.length - 1], bs = e && e.borderBoxSize && (e.borderBoxSize[0] || e.borderBoxSize);
      setPromoH(bs && typeof bs.blockSize === 'number' ? Math.round(bs.blockSize) : readPromoH());
    }).observe(promo);
  }

  /* History: the theme's own scroll handler (theme-header.js) decided
     docking from header.getBoundingClientRect().top <= 13, which our
     promo-bar offset (top: var(--promo-h)) made permanently false, and it
     stripped .bg and rewrote the logos' .active on every scroll event. This
     block used to restate the full result on every event and register a
     second listener at `load` so it ran after the theme's.

     28 Sep 2026 (verifier HDR-V3; the change itself is dated 27 Sep): that
     handler is gone from theme-header.js, and this file registers ONE scroll
     listener (onScroll, below). The load listener only restates the dock
     state a frame later; it is not a second scroll handler. This block is
     now the only writer of .bg, .fgo-docked and the logos' .active. */

  var dark  = document.querySelector('#headerLogo');
  var light = document.querySelector('#headerLogoAlt');

  /* Two fixes for the flashing, and they are different problems.

     1. THE FLASH. The theme's handler (removed 27 Sep 2026) stripped .bg on
        every scroll event and we re-added it. Because the header transitions `background` over .25s, each
        strip/re-add restarted a cross-fade — which is the black-to-cream
        oscillation. Reading state off .bg made us complicit: the class we were
        reading was being cleared underneath us every frame.

        So our CSS now keys on `fgo-docked`, a class the theme has never heard
        of and therefore cannot remove. We still set .bg so the theme's own
        rules and anything else depending on it keep working, but nothing we
        style is driven by it. Our visual state changes only when the scroll
        state actually changes.

     2. THE BAND. 18/6 gave twelve pixels of hysteresis. One wheel notch is
        50-120px and momentum scrolling oscillates around a boundary, so the
        band was easy to straddle. 90/40 cannot be crossed by accident. */
  var DOCK_AT = 90, RELEASE_AT = 40;
  var docked = null;                 /* null = not yet applied */
  var ticking = false;

  /* y is read ONCE per frame, before any class is written (run, below): window.scrollY forces style
     and layout in Chrome, so a read after the toggles here restyled the page mid-task */
  var apply = function (y) {
    var next = docked ? y >= RELEASE_AT : y > DOCK_AT;

    /* .bg is kept in sync for the theme's benefit, cheaply and every time */
    if (head.classList.contains('bg') !== next) head.classList.toggle('bg', next);

    /* .active must ALSO be restated every event, not just on a state change.
       It is shared with the theme's handler, which rewrites it on every scroll
       event. Restating it only on transitions meant the theme won every event
       after the first — leaving the dark mark active on the black docked bar,
       i.e. an invisible logo. Same shared-ownership trap as .bg; toggle with
       an explicit force value is idempotent, so doing it every time is free. */
    if (dark  && dark.classList.contains('active')  === next)  dark.classList.toggle('active', !next);
    if (light && light.classList.contains('active') !== next) light.classList.toggle('active', next);

    if (next === docked) return;     /* geometry already correct */
    docked = next;
    head.classList.toggle('fgo-docked', next);
    /* Same state on <html>, because the element that actually carries the
       sticky offset is the Shopify section wrapper ABOVE #header, and a
       parent is not selectable from a child in CSS. See fgo-header.css. */
    root.classList.toggle('fgo-head-docked', next);
  };

  /* rAF-throttled: scroll fires far faster than we can paint, and doing class
     work per event is what let the theme and this handler interleave mid-frame.

     A timer races the rAF because rAF starves in occluded windows, low-power
     mode and embedded panes — and a starved rAF here wedges `ticking` true
     forever, which kills docking for the life of the page. Whichever fires
     first wins; the loser no-ops. */
  var onScroll = function () {
    if (ticking) return;
    ticking = true;
    var done = false;
    var run = function () { if (done) return; done = true; ticking = false; var y = window.scrollY; apply(y); direction(y); };
    requestAnimationFrame(run);
    setTimeout(run, 120);
  };

  /* ---- DIRECTIONAL STICKY ------------------------------------------
     Hide on the way down, reveal on the way up — the reference's
     behaviour. Independent of docking: docking is a function of WHERE
     you are, this is a function of WHICH WAY you are going, and they
     compose (a revealed header at y=800 is still the docked variant).

     Three guards, each earning its place:
       DEADZONE  trackpads emit sub-pixel jitter in both directions;
                 without it the bar strobes when a finger rests still.
       REVEAL_AT above this the page is barely scrolled and hiding the
                 nav reads as a glitch rather than an intent.
       menu open never hide a bar the user is actively pointing at —
                 the mega panel is anchored to it and would fly away.  */
  /* 60, not 220. At 220 the bar survived two or three wheel notches, so it
     read as sticky-that-eventually-gives-up rather than as yielding to the
     reader. One notch is 50-120px, so 60 means the FIRST deliberate scroll
     down clears it — the reference's behaviour. Still above the noise floor,
     so a stray 10px nudge at the top of the page cannot trigger it. */
  var HIDE_BELOW = 60, DEADZONE = 6;
  var y0 = window.scrollY;           /* read before this script writes anything (READ, THEN WRITE) */
  var lastY = y0, hidden = false;

  var setHidden = function (want) {
    if (want === hidden) return;
    hidden = want;
    root.classList.toggle('fgo-head-hidden', want);
  };

  var direction = function (y) {
    var dy = y - lastY;
    if (Math.abs(dy) < DEADZONE) return;
    lastY = y;

    /* the panel is positioned off the header; hiding it mid-hover
       would drag the menu the user is reading off the screen. An open
       Shop panel counts too, however it was opened (keyboard, tap).
       27 Sep 2026: the :hover test stays. The audit suspected it of forcing a restyle each scroll
       frame; the trace stacks put this function's forced restyles (264-317 ms per desktop session
       at 4x, 11 per session, ~3,500 elements) on its window.scrollY read after apply()'s class
       writes, not on matches(). That read is gone (y comes from run), and a pointer-event flag in
       place of :hover would change when the bar hides after a tap on a touch screen. */
    var menuOpen = head.querySelector('#headerDrop') &&
                   (head.matches(':hover') || head.classList.contains('fgo-menu-open'));
    if (menuOpen) { setHidden(false); return; }

    if (y <= HIDE_BELOW) { setHidden(false); return; }
    setHidden(dy > 0);
  };

  /* a keyboard user tabbing into a hidden bar gets it back (25 Sep 2026) */
  head.addEventListener('focusin', function () { setHidden(false); });

  /* 27 Sep 2026: one scroll listener. The second, registered at load, existed to run after
     theme-header.js's own scroll handler, which rewrote .bg and the logos' .active on every event
     and read the header's rect to do it. That handler is gone (theme-header.js, same date): this
     block always had the last word on those classes, so what paints is unchanged, and the
     per-event class churn and rect read went with it. Load still restates the dock state (not the
     direction), in the next frame, where the scrollY read is the frame's own layout. */
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('load', function () { requestAnimationFrame(function () { apply(window.scrollY); }); });
  afterReads(function () { setPromoH(promoH0); apply(y0); });

  /* The top transition is gated behind this class (see fgo-header.css):
     first paint and the first measured correction must SNAP, not slide —
     a page should not open with its header animating into place. Double
     rAF = one fully painted frame with settled values; the timer covers
     contexts where rAF starves. Whichever fires first wins. */
  var readyOnce = false;
  var armReady = function () {
    if (readyOnce) return;
    readyOnce = true;
    root.classList.add('fgo-head-ready');
  };
  requestAnimationFrame(function () { requestAnimationFrame(armReady); });
  setTimeout(armReady, 300);
})();

/* ==================================================================
   CINEMA HERO — the header dissolves over a full-bleed film.

   Only two things are needed beyond the CSS: a flag saying this page
   opens on a cinematic hero, and the header's real height, because the
   hero is pulled up by exactly that much so the FILM grows behind the
   bar while the copy inside stays put.

   Height is measured, never assumed: the header's resting and docked
   states differ, the promo bar may or may not be present, and a
   hardcoded value would leave a gap or a crop on some template.
   ================================================================== */
(function () {
  var hero = document.querySelector('[data-fgo-cinema-hero]');
  if (!hero) return;

  var head = document.getElementById('header');
  if (!head) return;

  var root = document.documentElement;
  /* 27 Sep 2026: READ, THEN WRITE (see the dock block). read() measures, write() stores only a change;
     the first run reads while the script runs and writes in a microtask behind every other first read */
  var afterReads = window.queueMicrotask ? function (fn) { window.queueMicrotask(fn); } : function (fn) { Promise.resolve().then(fn); };
  var write = function (h) {
    if (h > 0 && root.style.getPropertyValue('--fgo-head-h') !== h + 'px') root.style.setProperty('--fgo-head-h', h + 'px');
  };

  var read = function () {
    /* the RESTING height is what the hero must offset. Reading it while
       docked would pull the hero up by the wrong amount for the rest of
       the page's life, so skip the write whenever the bar is docked and
       keep the last good value. */
    if (head.classList.contains('fgo-docked')) return 0;
    /* Measure the WRAPPER, not the bar. #header carries 12px top and 24px
       bottom margin, so its own height (117) is ~36px short of the space it
       actually occupies (153) — and the hero was pulled up by the smaller
       number, leaving the film starting 73px below the top instead of
       running behind the bar. The Shopify section wrapper's box includes
       those margins, so it is the honest measurement. */
    /* The wrapper's box does NOT include the bar's margins — with no padding
       or border on the wrapper they collapse straight out of it, so reading
       the wrapper returned the same 117 as the bar itself. Add the margins
       explicitly; that is the space the hero has to climb over. */
    var cs = getComputedStyle(head);
    return Math.round(
      head.getBoundingClientRect().height +
      (parseFloat(cs.marginTop) || 0) +
      (parseFloat(cs.marginBottom) || 0)
    );
  };
  var measure = function () { write(read()); };
  var t = 0;
  var afterFrame = function (fn) { var done = false; var run = function () { if (!done) { done = true; fn(); } }; requestAnimationFrame(function () { setTimeout(run, 0); }); setTimeout(run, 120); };
  var measureSoon = function () { if (!t) { t = 1; afterFrame(function () { t = 0; measure(); }); } };

  /* the dock block docks a page that opens below 90px in its own first write, which used to run
     before this measure and make it skip; the same test, read here before anything is written */
  var h0 = window.scrollY > 90 ? 0 : read();
  afterReads(function () { write(h0); root.classList.add('fgo-cinema-hero'); });

  /* the mark scales and the pill has responsive padding, so the resting
     height is not constant across breakpoints */
  if ('ResizeObserver' in window) {
    new ResizeObserver(measure).observe(head);   /* runs after layout: its reads are free */
  } else {
    window.addEventListener('resize', measureSoon, { passive: true });
  }
  /* fonts land after first paint and change the bar's height with them */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureSoon);
})();

/* ==================================================================
   HEADER OFFSET — one measured value, every template.

   The sticky offset was a fixed 49px. That happens to equal today's
   promo bar (37px) plus the header's 12px gap, so it looks right —
   until the promo copy wraps to two lines, the bar is switched off, or
   a template renders without it. Each hero treatment then sits at a
   slightly different top spacing, which is the margin drift that shows
   up "depending on the header treatment".

   Measuring the promo bar and publishing it as a variable makes the
   offset correct by construction on all three heroes and at any
   viewport, instead of correct by coincidence at one.
   ================================================================== */
(function () {
  var root = document.documentElement;
  var promo = document.querySelector('[id*="announcement"]');
  var wrap  = document.querySelector('[id*="theme-header"]');
  if (!wrap) return;

  /* 27 Sep 2026: READ, THEN WRITE (see the dock block). This block's write followed by measurePull's
     scrollY read was the second 2,560-element forced restyle on the phone lab (171 ms at 4x). */
  var afterReads = window.queueMicrotask ? function (fn) { window.queueMicrotask(fn); } : function (fn) { Promise.resolve().then(fn); };
  var read = function () { return promo ? Math.round(promo.getBoundingClientRect().height) : 0; };
  var write = function (h) { if (root.style.getPropertyValue('--fgo-promo-h') !== h + 'px') root.style.setProperty('--fgo-promo-h', h + 'px'); };
  var apply = function () { write(read()); };
  var t = 0;
  var afterFrame = function (fn) { var done = false; var run = function () { if (!done) { done = true; fn(); } }; requestAnimationFrame(function () { setTimeout(run, 0); }); setTimeout(run, 120); };
  var applySoon = function () { if (!t) { t = 1; afterFrame(function () { t = 0; apply(); }); } };

  var h0 = read();
  afterReads(function () { write(h0); });
  if ('ResizeObserver' in window && promo) {
    new ResizeObserver(function (entries) {
      var e = entries[entries.length - 1], bs = e && e.borderBoxSize && (e.borderBoxSize[0] || e.borderBoxSize);
      write(bs && typeof bs.blockSize === 'number' ? Math.round(bs.blockSize) : read());
    }).observe(promo);
  } else {
    window.addEventListener('resize', applySoon, { passive: true });
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(applySoon);
}());

  /* ---- HERO OVERLAY PULL (homepage) --------------------------------
     The hero must begin at the promo bar's lower edge so the header
     card floats ON the photograph, as the reference does. The header's
     section wrapper sits between them in the DOM and occupies flow
     height, so the hero is pulled up by exactly the distance from the
     promo bar's bottom to the wrapper's bottom.

     MEASURED, not assumed: that distance is the wrapper's own height
     plus whatever gap precedes it, and both move with the promo bar's
     height, the header's margins and the viewport. A hardcoded value
     was 40px short at this breakpoint alone.

     Read at scrollY 0 only — once anything is stuck the rects no
     longer describe the resting layout. */
  (function () {
    var root = document.documentElement;
    /* 27 Sep 2026: READ, THEN WRITE (see the dock block). readPull() only reads (scrollY, two rects);
       the first run's write waits for the microtask behind every other first read, and load / resize
       measure just after the next painted frame (see the dock block). The value is not rewritten when it has not changed: the inline twin
       in theme.liquid has usually written the same number before first paint. */
    var afterReads = window.queueMicrotask ? function (fn) { window.queueMicrotask(fn); } : function (fn) { Promise.resolve().then(fn); };
    var readPull = function () {
      if (window.scrollY > 0) return 0;
      var wrap  = document.querySelector('[id*="theme-header"]');
      var promo = document.querySelector('[id*="theme-announcement"]');
      if (!wrap) return 0;
      var wb = wrap.getBoundingClientRect().bottom;
      var pb = promo ? promo.getBoundingClientRect().bottom : 0;
      return Math.round(wb - pb);
    };
    var writePull = function (pull) {
      if (pull > 0 && root.style.getPropertyValue('--fgo-head-pull') !== pull + 'px') root.style.setProperty('--fgo-head-pull', pull + 'px');
    };
    var t = 0;
    var afterFrame = function (fn) { var done = false; var run = function () { if (!done) { done = true; fn(); } }; requestAnimationFrame(function () { setTimeout(run, 0); }); setTimeout(run, 120); };
    var measurePull = function () { if (!t) { t = 1; afterFrame(function () { t = 0; writePull(readPull()); }); } };
    var pull0 = readPull();
    afterReads(function () { writePull(pull0); });
    addEventListener('load', measurePull);
    addEventListener('resize', measurePull);
  })();

/* ==================================================================
   MENU INTENT — the Shop panel's state machine (22 Aug night; rebuilt
   25 Sep 2026 with the Shop menu "option C").

   MOUSE: hover, as before. Opening needs a 90ms dwell on SHOP or its
   chevron (a drive-by crosses the zone in 30-80ms and never fires);
   leaving the header starts a 200ms grace that re-entering the bar or
   the panel CANCELS, so there is never a half-faded panel to catch.
   The other top links close it at once.

   THE CHEVRON is SHOP's disclosure button (SHOP itself stays a link to
   /collections/all): a click or tap toggles, Enter / Space toggle and,
   when opening, put focus on the first panel link. A click on a panel
   the hover already opened keeps it open rather than shutting it.
   SHOP (26 Sep 2026): Enter follows the link, as on any link; Space or
   the down arrow open the panel from it and put focus on the first link.
   Both SHOP and the chevron carry aria-expanded / aria-controls.

   KEYBOARD: Tab from the chevron walks the links then the cards, in the
   order they read; Tab past the last card closes the panel and goes on
   to the next top link; Shift+Tab from the first link goes back to the
   chevron; Escape closes and returns focus to whichever of SHOP and the
   chevron opened it (the chevron when the mouse did); focus leaving the
   header closes it. The closed panel is visibility:hidden, so none of it
   is focusable.

   TOUCH (hover: none) at 1080px and up: the tap on the chevron toggles,
   a tap anywhere outside closes, a tap on SHOP just follows the link.
   Hover is read per event (pointerType 'mouse'), so a hybrid laptop's
   touchscreen never opens it by accident.

   The only output is #header.fgo-menu-open + aria-expanded. CSS:
   theme-header.css, SHOP PANEL. The card images wait in data-* and are
   moved in on the first sign of intent (pointer on the header, focus in
   the nav, a press on the chevron), so they cost nothing on page load.
   ================================================================== */
(function () {
  var head = document.getElementById('header');
  var drop = document.getElementById('headerDrop');
  var shop = document.getElementById('headerShop');
  var btn = document.getElementById('headerShopToggle');
  if (!head || !drop || !shop || !btn || !window.matchMedia) return;
  document.documentElement.classList.add('fgo-menu-js');

  var wide = matchMedia('(min-width: 1080px)');
  var OPEN_DWELL = 90, CLOSE_GRACE = 200, HOVER_CLICK = 700;
  var openT = null, closeT = null, how = '', openedAt = 0, viaKey = false;
  var opener = null;   /* SHOP or the chevron, whichever opened (or claimed) the panel: Escape gives focus back there */
  var now = function () { return window.performance && performance.now ? performance.now() : Date.now(); };
  var mouse = function (e) { return e.pointerType === 'mouse'; };
  /* a focus move forces a restyle; made just after the panel's first frame, the key press is
     answered a frame sooner and the focus lands one frame later (Event Timing, 25 Sep) */
  var afterPaint = function (fn) {
    var done = false;
    var run = function () { if (done) return; done = true; fn(); };
    requestAnimationFrame(function () { setTimeout(run, 0); });
    setTimeout(run, 120);
  };
  var isOpen = function () { return head.classList.contains('fgo-menu-open'); };

  var warmed = false;
  var warm = function () {
    if (warmed || !wide.matches) return;   /* below 1080 the panel is not shown: its cards stay unfetched */
    warmed = true;
    var imgs = drop.querySelectorAll('img[data-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.loading = 'eager';
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  };

  var setOpen = function (on, by) {
    if (on && !wide.matches) on = false;
    if (on) warm();
    if (on === isOpen()) { if (on && by) how = by; return; }
    head.classList.toggle('fgo-menu-open', on);
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    shop.setAttribute('aria-expanded', on ? 'true' : 'false');   /* SHOP carries the state too (26 Sep 2026) */
    how = on ? (by || 'hover') : '';
    if (!on) opener = null;
    openedAt = now();
  };
  var hold = function () { if (closeT) { clearTimeout(closeT); closeT = null; } };
  var disarm = function () { if (openT) { clearTimeout(openT); openT = null; } };
  var shut = function () { disarm(); hold(); setOpen(false); };

  var wantOpen = function () {
    hold();
    if (isOpen() || openT) return;
    openT = setTimeout(function () { openT = null; setOpen(true, 'hover'); }, OPEN_DWELL);
  };
  var wantClose = function () {
    disarm();
    if (!isOpen() || how === 'key') return;
    hold();
    closeT = setTimeout(function () { closeT = null; setOpen(false); }, CLOSE_GRACE);
  };

  /* hover (mouse only); the cards are fetched on the first pointer on the bar or focus on SHOP */
  head.addEventListener('pointerenter', function () { warm(); }, { passive: true });
  shop.addEventListener('focus', warm);
  btn.addEventListener('focus', warm);
  [shop, btn].forEach(function (el) {
    el.addEventListener('pointerenter', function (e) { if (mouse(e)) wantOpen(); }, { passive: true });
    el.addEventListener('pointerleave', function (e) { if (mouse(e) && !isOpen()) disarm(); }, { passive: true });
  });
  /* wandering the bar or the panel while open keeps it open; leaving the header starts the grace */
  head.addEventListener('pointermove', function (e) { if (mouse(e) && isOpen()) hold(); }, { passive: true });
  head.addEventListener('pointerleave', function (e) { if (mouse(e)) wantClose(); }, { passive: true });

  /* 19 Sep 2026: the other top links (Subscribe & Save, Bundles, Recipes) have no menu of their own, so reaching one
     by pointer or keyboard closes an open Shop panel at once instead of waiting out the grace */
  Array.prototype.forEach.call(document.querySelectorAll('#headerInnerLeft .headerRightNavLink'), function (a) {
    a.addEventListener('pointerenter', function (e) { if (mouse(e)) shut(); }, { passive: true });
    a.addEventListener('focus', shut);
  });

  /* the chevron */
  btn.addEventListener('pointerdown', function () { viaKey = false; warm(); }, { passive: true });
  btn.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') viaKey = true;
    if (e.key === 'Tab' && !e.shiftKey && isOpen()) {
      var f = focusables();
      if (f.length) { e.preventDefault(); f[0].focus(); }
    }
  });
  btn.addEventListener('click', function (e) {
    var byKey = viaKey || e.detail === 0;
    viaKey = false;
    disarm(); hold();
    opener = btn;
    if (isOpen()) {
      /* the hover got there first: this click means "yes, this menu", not "close it" */
      if (!byKey && how === 'hover' && now() - openedAt < HOVER_CLICK) { how = 'click'; return; }
      setOpen(false);
      return;
    }
    setOpen(true, byKey ? 'key' : 'click');
    if (byKey) afterPaint(function () { var f = focusables(); if (isOpen() && f.length) f[0].focus(); });
  });

  /* only what can take focus: from 1080 to 1199px the second card is display:none (theme-header.css,
     SHOP PANEL), and counting it as the last stop left Tab from the real last card inside the header
     with the panel open (26 Sep 2026) */
  var focusables = function () {
    return Array.prototype.filter.call(drop.querySelectorAll('a[href], button:not([disabled])'), function (el) {
      return el.checkVisibility ? el.checkVisibility() : el.offsetWidth > 0;
    });
  };
  var nextTop = function () {
    var el = btn.nextElementSibling;
    while (el && !(el.matches('a[href], button') && el.offsetWidth)) el = el.nextElementSibling;
    return el || document.getElementById('headerLogo');
  };

  /* SHOP itself (26 Sep 2026): Space or the down arrow open the panel and put focus on its first link
     (Space would otherwise scroll the page; a link does nothing with it). Enter is not touched: SHOP
     is a link by Nate's decision, and Enter follows it. */
  shop.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'ArrowDown' && e.key !== 'Down') return;
    e.preventDefault();
    disarm(); hold();
    opener = shop;
    setOpen(true, 'key');
    afterPaint(function () { var f = focusables(); if (isOpen() && f.length) f[0].focus(); });
  });

  /* a panel the mouse opened becomes the keyboard's once focus is walking it: the pointer
     wandering off must not hide the link that has focus */
  drop.addEventListener('focusin', function (e) {
    if (!isOpen() || how === 'key') return;
    var kb = false;
    try { kb = e.target.matches(':focus-visible'); } catch (x) { kb = false; }
    if (kb) how = 'key';
  });

  /* inside the panel: Tab past the last card leaves for the next top link; Shift+Tab off the first link returns */
  drop.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
      e.preventDefault();
      shut();
      var n = nextTop();
      if (n) n.focus();
    } else if (e.shiftKey && document.activeElement === f[0]) {
      e.preventDefault();
      btn.focus();
    }
  });

  /* Escape closes; if focus was in the menu it goes back to whichever of SHOP and the chevron opened
     the panel (the chevron when the mouse did) */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !isOpen()) return;
    var a = document.activeElement;
    var mine = a === btn || a === shop || drop.contains(a);
    var back = opener && opener.offsetWidth ? opener : btn;
    shut();
    if (mine) afterPaint(function () { back.focus(); });
  });
  /* focus leaving the header closes */
  head.addEventListener('focusout', function (e) {
    if (isOpen() && (!e.relatedTarget || !head.contains(e.relatedTarget))) {
      if (!e.relatedTarget && how !== 'key') return;   /* a click on the page: the pointer rules decide */
      shut();
    }
  });
  /* a press anywhere outside the panel and its chevron closes (touch, and a pinned click) */
  document.addEventListener('pointerdown', function (e) {
    if (!isOpen()) return;
    var t = e.target;
    if (drop.contains(t) || btn.contains(t) || shop.contains(t)) return;
    if (mouse(e) && head.contains(t)) return;   /* the mouse on the bar: hover rules */
    shut();
  }, { capture: true, passive: true });

  /* the search panel drops from the same edge: opening it closes the Shop panel */
  var search = document.getElementById('headerRightIconsSearch');
  if (search) search.addEventListener('click', shut);

  /* leaving the desktop band (a window narrowed, a tablet turned) closes it */
  var onBand = function () { if (!wide.matches) shut(); };
  if (wide.addEventListener) wide.addEventListener('change', onBand); else if (wide.addListener) wide.addListener(onBand);
  /* back/forward cache: never come back to an open panel */
  window.addEventListener('pageshow', function (e) { if (e.persisted) shut(); });
}());

/* OFF-SCREEN LOOPS OFF (19 Sep 2026). Marks every section that is well off screen with .is-offscreen, so the
   rule in theme-global.css can switch its decorative loops off. Why it lives here: a loop running anywhere on
   the page kept the header on a separate GPU layer, which drew the nav text soft until hovered. */
(function () {
  if (!('IntersectionObserver' in window)) return;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('is-offscreen', !e.isIntersecting); });
  }, { rootMargin: '150px 0px' });
  var watch = function () { document.querySelectorAll('.shopify-section').forEach(function (s) { io.observe(s); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  document.addEventListener('shopify:section:load', function (e) { if (e.target && e.target.classList) io.observe(e.target); });
}());

/* ==================================================================
   NAV BEAD (19 Sep 2026): one indicator per nav group. See NAV BEAD in
   fgo-header.css for the look. This owns where it is and what shape:
     pressed / pointed at / keyboard-focused / SHOP's menu open
                                                      -> capsule there
     otherwise the page you are on (aria-current)    -> line there
     otherwise                                        -> folds and fades
   Hover follows a mouse only (pointerType 'mouse', the per-event form of
   (hover: hover) and (pointer: fine)), so a tablet tap can never leave a
   hover behind. A finger or pen gets press feedback instead: the capsule
   blooms in place under the finger (no glide from the current page) and
   lets go 160ms after the lift. Keyboard focus counts only when
   :focus-visible, and any press hands focus back to the pointer (ring off).
   Geometry is measured, snapped to device pixels, and re-measured when
   anything in the row resizes (fonts, breakpoints, docking).
   ================================================================== */
(function () {
  'use strict';
  var head = document.getElementById('header');
  if (!head || !window.matchMedia || !head.getBoundingClientRect) return;

  var desk = matchMedia('(min-width: 801px)');
  var still = matchMedia('(prefers-reduced-motion: reduce)');
  var LINE_H = 3, ICON_LINE = 14, CAPS_LIFT = .07, GRACE = 70, FADE = 90, RELEASE = 160;   /* capsule height and side padding come from CSS: --fgo-bead-h, --fgo-bead-pad */
  var now = function () { return window.performance && performance.now ? performance.now() : Date.now(); };
  var renders = [];

  var group = function (host, sel, pin) {
    if (!host) return;
    var list = Array.prototype.slice.call(host.querySelectorAll(sel));
    if (!list.length) return;
    var bead = document.createElement('span');
    bead.className = 'fgoNavBead';
    bead.setAttribute('aria-hidden', 'true');
    host.insertBefore(bead, host.firstChild);

    var hover = null, focus = null, press = null, hoverAt = 0, focusAt = 0;
    var last = null, lastBloom = false, lastKey = '', leaveT = 0, fadeT = 0, pressT = 0;

    /* 26 Sep 2026 (INP audit 3). Four changes to how the row is measured, none to what it shows:
         - the pointer and focus handlers only note what happened and ask for a frame (schedule),
           so however many of them fire for one press (pointerenter, pointerdown, focus, focusin
           all did, each measuring the row and each forcing a layout: 43 + 63 + 38ms on a Shop
           click, the audit measured on production) the row is measured ONCE, in the next frame,
           every read before any write;
         - on phones (under 801px, the bead is display:none) nothing is read or written at all;
         - the capsule's height and reach (--fgo-bead-h, --fgo-bead-pad) change only at a
           breakpoint, so they are read when the row is re-measured (an instant render: fonts,
           resize, breakpoint), not on every render;
         - the `void bead.offsetWidth` reflow, which committed a snapped position before the
           next write, is gone: the snapped write wears is-snap (transition: none) for one frame
           and the follow-up write lands in the frame after, which is what the reflow bought.
       The ResizeObserver still renders on the spot: it runs after layout, where reads are free. */
    var CAP_H = 34, PAD_X = 14;
    var readVars = function () {
      var hs = getComputedStyle(host);
      CAP_H = parseFloat(hs.getPropertyValue('--fgo-bead-h')) || 34;
      PAD_X = parseFloat(hs.getPropertyValue('--fgo-bead-pad')) || 14;
    };
    var live = function (el) { return !!el && el.offsetWidth > 0 && list.indexOf(el) > -1; };
    var home = function () {
      for (var i = 0; i < list.length; i++) if (live(list[i]) && list[i].getAttribute('aria-current') === 'page') return list[i];
      return null;
    };

    /* READ: where the line or the capsule goes for `el`. No writes in here. */
    var geom = function (el, bloom) {
      var dpr = window.devicePixelRatio || 1;
      var snap = function (v) { return Math.round(v * dpr) / dpr; };
      var hr = host.getBoundingClientRect(), r = el.getBoundingClientRect();
      var ox = hr.left + host.clientLeft, oy = hr.top + host.clientTop + host.clientHeight;
      var cx, cy, w;
      if (el.tagName === 'BUTTON') {                       /* icon: a circle on the glyph */
        var s = el.querySelector('svg'), sr = s ? s.getBoundingClientRect() : r;
        cx = sr.left + sr.width / 2; cy = sr.top + sr.height / 2;
        w = bloom ? CAP_H : ICON_LINE;
      } else {                                             /* word: caps sit ~.07em above the box centre */
        var cs = getComputedStyle(el), tw = r.width;
        /* SHOP ends on its chevron, which is its own button since 25 Sep 2026 (data-bead-tail): the
           ink runs to the chevron's right edge, so the capsule covers the pair exactly as it did */
        var tailId = el.getAttribute('data-bead-tail'), tail = tailId && document.getElementById(tailId);
        if (tail && tail.offsetWidth) {
          tw = (tail.querySelector('svg') || tail).getBoundingClientRect().right - r.left;
        } else {
          /* tracking hangs one letter-space past the last letter; measure the ink, not the box */
          var end = el.lastChild;
          while (end && end.nodeType === 3 && !end.nodeValue.trim()) end = end.previousSibling;
          if (end && end.nodeType === 3) tw -= parseFloat(cs.letterSpacing) || 0;
        }
        cx = r.left + tw / 2;
        cy = r.top + r.height / 2 - CAPS_LIFT * (parseFloat(cs.fontSize) || 14);
        w = bloom ? tw + 2 * PAD_X : tw;
      }
      w = snap(w);
      var left = snap(cx - w / 2), bottom = snap(cy + CAP_H / 2);   /* line and capsule share one bottom edge */
      return { l: left - ox, w: w, b: oy - bottom, h: bloom ? CAP_H : LINE_H };
    };

    /* the frame after a snapped write: is-snap comes off, and whatever was queued for "after the
       snap" (the bloom that follows an appearance) is written then, so it transitions from it */
    var frameT = 0, queued = null;
    var nextFrame = function (fn) {
      if (fn) queued = fn;
      if (frameT) return;
      frameT = requestAnimationFrame(function () {
        frameT = 0;
        bead.classList.remove('is-snap');
        var f = queued; queued = null;
        if (f) f();
      });
    };

    /* WRITE: g from geom (null keeps the place and changes the state). snap = no transition. */
    var apply = function (g, bloom, on, ring, snap) {
      var key = (g ? [g.l, g.w, g.b, g.h].join(',') : lastKey.split('|')[0]) + '|' + bloom + on + ring;
      if (key === lastKey && !snap) return;
      lastKey = key;
      if (snap) bead.classList.add('is-snap');
      if (g) {
        bead.style.left = g.l + 'px'; bead.style.width = g.w + 'px';
        bead.style.bottom = g.b + 'px'; bead.style.height = g.h + 'px';
      }
      bead.classList.toggle('is-bloom', bloom);
      bead.classList.toggle('is-on', on);
      bead.classList.toggle('is-key', ring);
      if (snap) nextFrame(null);
    };

    var pick = function () {
      if (live(press)) return press;
      var h = live(hover) ? hover : null, f = live(focus) ? focus : null;
      if (h && f) return hoverAt >= focusAt ? h : f;
      return h || f || (pin && live(pin()) ? pin() : null);
    };

    /* inPlace: a finger went down. Never glide under it: the current page's
       line goes at once and the capsule blooms from a line at the finger. */
    var render = function (instant, inPlace) {
      if (!desk.matches) {                                 /* phones: the bead is display:none; nothing to read */
        if (fadeT) { clearTimeout(fadeT); fadeT = 0; }
        if (lastKey) { lastKey = ''; bead.classList.remove('is-bloom', 'is-on', 'is-key'); }
        last = null; lastBloom = false;
        return;
      }
      if (instant) readVars();
      var t = pick(), el = t || home(), bloom = !!t, ring = !!t && t === focus;
      if (!el) {                                           /* nothing to mark: fold into a line where it is, then fade */
        clearTimeout(fadeT); fadeT = 0;
        if (live(last)) apply(geom(last, false), false, false, false, !!instant);
        else apply(null, false, false, false, true);
        return;
      }
      var showing = parseFloat(getComputedStyle(bead).opacity) > .02;
      var gLine = geom(el, false), gFull = bloom ? geom(el, true) : gLine;   /* every read, before any write */
      var jump = inPlace && (el !== last || !lastBloom);
      if (!showing || instant || jump) {                   /* appear AS the line at the target, then bloom from it */
        clearTimeout(fadeT); fadeT = 0;
        if (instant) { apply(gFull, bloom, true, ring, true); last = el; lastBloom = bloom; return; }
        apply(gLine, false, false, false, true);
        nextFrame(function () { apply(gFull, bloom, true, ring, false); });
        last = el; lastBloom = bloom;
        return;
      } else if (still.matches && (el !== last || bloom !== lastBloom)) {
        clearTimeout(fadeT);                               /* reduced motion: out, move, in */
        bead.classList.remove('is-on'); lastKey = '';
        fadeT = setTimeout(function () { fadeT = 0; apply(gLine, false, false, false, true); nextFrame(function () { apply(gFull, bloom, true, ring, false); }); }, FADE);
        last = el; lastBloom = bloom;
        return;
      }
      if (fadeT) return;
      apply(gFull, bloom, true, ring, false);
      last = el; lastBloom = bloom;
    };
    /* one render per frame, whatever asked for it; an instant or in-place ask carries over */
    var raf = 0, wantInstant = false, wantInPlace = false;
    var schedule = function (instant, inPlace) {
      wantInstant = wantInstant || !!instant; wantInPlace = wantInPlace || !!inPlace;
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; var i = wantInstant, p = wantInPlace; wantInstant = wantInPlace = false; render(i, p); });
    };
    var letGo = function () {
      clearTimeout(pressT); pressT = 0;
      if (!press) return;
      pressT = setTimeout(function () { pressT = 0; press = null; schedule(); }, RELEASE);
    };
    renders.push({ schedule: schedule, reset: function () {
      clearTimeout(leaveT); clearTimeout(pressT); pressT = 0;
      hover = null; focus = null; press = null; bead.classList.remove('is-press'); schedule(true);
    } });

    /* src is what the pointer touches, el the link the bead marks: the same element, or an alias
       (SHOP's chevron button, data-bead-alias, 25 Sep 2026) that counts as part of that link */
    var bind = function (src, el) {
      src.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !desk.matches) return;
        clearTimeout(leaveT); hover = el; hoverAt = now(); schedule();
      });
      src.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse' || !desk.matches) return;
        bead.classList.remove('is-press');
        clearTimeout(leaveT);
        leaveT = setTimeout(function () { if (hover === el) { hover = null; schedule(); } }, GRACE);
      });
      src.addEventListener('pointerdown', function (e) {
        if (!desk.matches) return;                         /* phones: nothing to draw, nothing to measure */
        var hadRing = !!focus;
        focus = null;                                      /* a press hands focus back to the pointer: no ring */
        if (e.pointerType === 'mouse') {
          if (e.button === 0 && hover === el) bead.classList.add('is-press');
          if (hadRing || hover !== el) schedule();         /* the hover already drew the capsule here */
          return;
        }
        clearTimeout(pressT); pressT = 0;                  /* finger or pen: press feedback, in place */
        press = el; schedule(false, true);
      });
    };
    list.forEach(function (el) { bind(el, el); });
    var aliases = Array.prototype.filter.call(host.querySelectorAll('[data-bead-alias]'), function (src) {
      var el = document.getElementById(src.getAttribute('data-bead-alias'));
      if (list.indexOf(el) < 0) return false;
      bind(src, el);
      return true;
    });
    host.addEventListener('focusin', function (e) {
      if (!desk.matches || list.indexOf(e.target) < 0) return;
      var vis = false;
      try { vis = e.target.matches(':focus-visible'); } catch (x) { vis = false; }
      var f = vis ? e.target : null;
      if (!f && !focus) return;                            /* a click's focus: the pointer already has it marked */
      focus = f; focusAt = now(); schedule();
    });
    host.addEventListener('focusout', function (e) {
      if (!desk.matches || e.target !== focus || list.indexOf(e.relatedTarget) > -1) return;   /* focusin moves it */
      focus = null; schedule();
    });
    document.addEventListener('pointerup', function (e) {
      if (!desk.matches) return;
      bead.classList.remove('is-press');
      if (e.pointerType !== 'mouse') letGo();
    }, true);
    document.addEventListener('pointercancel', function (e) { if (desk.matches && e.pointerType !== 'mouse') letGo(); }, true);
    window.addEventListener('blur', function () {
      if (!desk.matches) return;
      clearTimeout(pressT); pressT = 0;
      hover = null; press = null; bead.classList.remove('is-press'); schedule();
    });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { render(true); });
      ro.observe(host); list.forEach(function (el) { ro.observe(el); }); aliases.forEach(function (el) { ro.observe(el); });
    } else {
      window.addEventListener('resize', function () { schedule(true); }, { passive: true });
    }
    /* 27 Sep 2026: no render at the START of the first frame. The ResizeObserver below already
       renders in that frame, after layout, where its reads are free; the extra one scheduled here
       ran first, and on a desktop load its getComputedStyle was the first thing to ask for style
       after the header's own writes, so it paid the page's whole first restyle as forced work
       (2,672 elements, up to 870 ms at 4x). What is left is a backstop just after the first
       painted frame, for a browser without ResizeObserver. */
    var first = false;
    var firstRun = function () { if (first) return; first = true; render(true); };
    requestAnimationFrame(function () { setTimeout(firstRun, 0); });
    setTimeout(firstRun, 120);
  };

  document.documentElement.classList.add('fgo-bead-js');
  var shop = document.getElementById('headerShop');
  group(document.getElementById('headerInnerLeft'), '#headerInnerLeft > a', function () {
    return head.classList.contains('fgo-menu-open') ? shop : null;
  });
  group(document.getElementById('headerRight'), '#headerRightNav > a, #headerRightIcons > button');

  var all = function (instant) { renders.forEach(function (g) { g.schedule(instant); }); };
  /* SHOP's menu opening and closing (fgo-header.js MENU INTENT toggles the class). The theme also
     rewrites .bg on #header every scroll event, so only a change in the menu state is acted on. */
  var wasOpen = head.classList.contains('fgo-menu-open');
  /* read after the panel's first frame (25 Sep 2026): the bead measures the row, and doing that
     inside the click forced a restyle the panel's first frame did not need */
  var later = function (fn) { var done = false; var run = function () { if (!done) { done = true; fn(); } }; requestAnimationFrame(function () { setTimeout(run, 0); }); setTimeout(run, 120); };
  if (window.MutationObserver) new MutationObserver(function () {
    var open = head.classList.contains('fgo-menu-open');
    if (open !== wasOpen) { wasOpen = open; later(function () { all(false); }); }
  }).observe(head, { attributes: true, attributeFilter: ['class'] });
  var onDesk = function () { all(true); };
  if (desk.addEventListener) desk.addEventListener('change', onDesk); else if (desk.addListener) desk.addListener(onDesk);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(onDesk).catch(function () {});
  /* back/forward cache: the link you clicked still thinks it is hovered (or pressed) */
  window.addEventListener('pageshow', function (e) { if (e.persisted) renders.forEach(function (g) { g.reset(); }); });
}());

/* ==================================================================
   DRAWER BAND EXIT (23 Sep 2026). The drawer serves every width up to
   1079px now (fgo-header.css, TABLET BAND). Open it on an iPad in portrait,
   turn the iPad to landscape, and the viewport leaves the band: the drawer
   is display:none there but body.noScroll stays on, so the page will not
   scroll. Leaving the band shuts the drawer the theme's own way (its X).
   ================================================================== */
(function () {
  'use strict';
  if (!window.matchMedia) return;
  var wide = matchMedia('(min-width: 1080px)');
  var shut = function () {
    if (!wide.matches) return;
    var slider = document.getElementById('slider'), x = document.getElementById('headerClose');
    if (slider && x && slider.classList.contains('active')) x.click();
  };
  if (wide.addEventListener) wide.addEventListener('change', shut); else if (wide.addListener) wide.addListener(shut);
}());
